// 면접 평가지 모달 — 스케줄/리스트 모달 위에 한 겹 더. 저장 버튼 없이 변경 즉시 디바운스 자동 저장.
// 상태·평가 리스트 종류는 면접자 공통, 등급·메모는 평가자별(서버가 로그인 사용자 본인 평가로 저장).
// 평가자 탭: '내 평가'(입력 가능) + 이 면접자를 평가한 다른 직원(보기만).
import React, { useEffect, useRef, useState } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import { toast } from 'react-toastify';
import styles from '../../componentsStyle/interviewschedule.module.css';
import { postFetch } from '../../../fetch/fetch';
import { EVAL_LISTS, InterviewEvaluation, InterviewItem, categoryOf, fmtWhen, hasGrades, shortLocation } from './interviewconstants';
import { captureEvaluation } from './interviewcapture';
import InterviewSelect from './interviewselect';

const evalBoxStyle: any = {
    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
    width: 'calc(100% - 48px)', maxWidth: 520, maxHeight: 'calc(100vh - 48px)', overflowY: 'auto',
    bgcolor: '#fff', borderRadius: '16px', boxShadow: 24, outline: 'none',
    p: '20px 22px 22px', display: 'flex', flexDirection: 'column', gap: '14px', boxSizing: 'border-box'
};

const STATUS = [
    { label: "예정", v: 0, color: "#3346A6" },
    { label: "면접 완료", v: 1, color: "#17643a" },
    { label: "노쇼", v: 2, color: "#a32218" },
];

const SAVE_DELAY = 500;

interface Props {
    item: InterviewItem;
    user: any;
    onLocalChange: (id: number, patch: Partial<InterviewItem>) => void;
    onDeleted: (id: number) => void;
    onClose: () => void;
}

const InterviewEvalModal: React.FC<Props> = ({ item, user, onLocalChange, onDeleted, onClose }) => {

    const myId: number = user?.id;
    const myName: string = user?.name || "";

    const [viewId, setViewId] = useState<number>(myId);
    const [capMsg, setCapMsg] = useState("");
    const [capImg, setCapImg] = useState("");
    const pendingRef = useRef<any>({});
    const timerRef = useRef<any>(null);

    const flush = async () => {
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
        const patch = pendingRef.current;
        pendingRef.current = {};
        if (!Object.keys(patch).length) return;
        try {
            const result: any = await postFetch("/dashboard/interview/update", { id: item.id, ...patch });
            if (result.message !== "success") {
                toast.error("평가 저장에 실패했습니다.");
            }
        } catch (e) {
            console.log(e);
            toast.error("평가 저장에 실패했습니다.");
        }
    };

    // 닫힘/언마운트 시 남은 변경 저장
    useEffect(() => {
        return () => { flush(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 서버로 보낼 변경 모으기 (디바운스)
    const queue = (patch: any) => {
        pendingRef.current = { ...pendingRef.current, ...patch };
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(flush, SAVE_DELAY);
    };

    const category = categoryOf(item);
    const evalItems = category ? EVAL_LISTS[category] : [];
    const evaluations = item.evaluations || [];

    const mine: InterviewEvaluation = evaluations.find((ev) => ev.evaluatorId === myId)
        || { evaluatorId: myId, evaluatorName: myName, grades: [], memo: "" };
    const others = evaluations.filter((ev) => ev.evaluatorId !== myId);
    const viewEval = viewId === myId ? mine : (others.find((ev) => ev.evaluatorId === viewId) || mine);
    const readOnly = viewEval.evaluatorId !== myId;
    const viewName = readOnly ? viewEval.evaluatorName : myName;
    // 항목 수가 안 맞는 등급(종류 바뀌기 전 값)은 빈칸으로
    const viewGrades = viewEval.grades.length === evalItems.length ? viewEval.grades : evalItems.map(() => "");

    // 내 평가 화면 반영(없으면 추가) + 저장
    const changeMine = (patch: Partial<InterviewEvaluation>) => {
        const next = { ...mine, ...patch };
        const exists = evaluations.some((ev) => ev.evaluatorId === myId);
        onLocalChange(item.id, { evaluations: exists ? evaluations.map((ev) => ev.evaluatorId === myId ? next : ev) : [...evaluations, next] });
        queue(patch);
    };

    const pickGrade = (i: number, g: string) => {
        if (readOnly) return;
        const grades = evalItems.map((_, idx) => viewGrades[idx] || "");
        grades[i] = grades[i] === g ? "" : g;
        changeMine({ grades });
    };

    const changeStatus = (status: number) => {
        onLocalChange(item.id, { status });
        queue({ status });
    };

    // 평가 리스트 종류 변경 → 항목 수가 달라지므로 모든 평가자 등급 초기화
    const changeCategory = (v: string) => {
        if (v === category) return;
        if (evaluations.some(hasGrades) && !window.confirm("평가 리스트 종류를 바꾸면 항목 수가 달라져서 이 면접자의 모든 평가자 등급이 초기화됩니다. 바꿀까요?")) return;
        delete pendingRef.current.grades;
        onLocalChange(item.id, { category: v, evaluations: evaluations.map((ev) => ({ ...ev, grades: [] })) });
        pendingRef.current = { ...pendingRef.current, category: v };
        flush();
    };

    const capture = async () => {
        if (!category) { setCapMsg("평가 리스트 종류를 먼저 고르세요."); setCapImg(""); return; }
        await flush();
        const result = await captureEvaluation(item, category, viewGrades, viewEval.memo || "", viewName);
        if (result.ok) {
            setCapMsg(`${viewName} 평가지 이미지를 클립보드에 복사했습니다. 카톡방에 붙여넣기 하세요.`);
            setCapImg("");
        } else {
            setCapMsg("이 환경에서는 클립보드 복사가 안 돼서 아래에 이미지를 띄웠습니다. 우클릭해서 복사하세요.");
            setCapImg(result.dataUrl);
        }
    };

    const remove = async () => {
        if (!window.confirm(`${item.name} 면접을 삭제할까요?`)) return;
        pendingRef.current = {};
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
        const result: any = await postFetch("/dashboard/interview/delete", { id: item.id });
        if (result.message === "success") {
            onDeleted(item.id);
        } else {
            toast.error("삭제에 실패했습니다.");
        }
    };

    const tabs = [{ id: myId, label: "내 평가" }, ...others.map((ev) => ({ id: ev.evaluatorId, label: ev.evaluatorName }))];

    return (
        <Modal open onClose={onClose} sx={{ zIndex: 700 }}>
            <Box sx={evalBoxStyle} className={styles.wrap} role="dialog" aria-label="면접 평가">
                <div className={styles.evalHead}>
                    <div>
                        <div className={styles.evalNameRow}>
                            <div className={styles.evalName}>{item.name}</div>
                            <button className={styles.captureBtn} onClick={capture} title="보고 있는 평가지를 이미지로 복사">
                                <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#3346A6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 8h3l2-3h6l2 3h3v11H4z"></path><circle cx="12" cy="13" r="3.5"></circle></svg>
                                캡처
                            </button>
                        </div>
                        <div className={styles.evalSub}><span className={styles.loc}>{shortLocation(item.location)}</span> {item.job} · {fmtWhen(item)}</div>
                    </div>
                    <button className={styles.circleBtn} aria-label="닫기" onClick={onClose}>
                        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#333333" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18"></path></svg>
                    </button>
                </div>

                <div className={styles.statusRow}>
                    {STATUS.map((s) => {
                        const active = item.status === s.v;
                        return (
                            <button key={s.v} className={styles.statusBtn} onClick={() => changeStatus(s.v)}
                                style={active ? { borderColor: s.color, background: s.color, color: "#fff" } : undefined}>
                                {s.label}
                            </button>
                        );
                    })}
                </div>
                <div className={styles.statusHint}>상태는 공통, 아래 평가·메모는 평가자별로 따로 저장됩니다.</div>

                <div className={styles.catRow}>
                    <label htmlFor="interview-cat-select" className={styles.catLabel}>평가 리스트</label>
                    <InterviewSelect id="interview-cat-select" value={category} height={40} fullWidth
                        options={[{ value: "", label: "미정 (근무 종류 선택)" }, { value: "당직", label: "당직" }, { value: "사감", label: "사감" }, { value: "데스크", label: "데스크" }]}
                        onChange={changeCategory} />
                </div>

                <div className={styles.tabRow}>
                    {tabs.map((t) => (
                        <button key={t.id} className={`${styles.tab} ${t.id === viewEval.evaluatorId ? styles.tabActive : ""}`} onClick={() => setViewId(t.id)}>{t.label}</button>
                    ))}
                </div>

                {!category ? (
                    <div className={styles.noCat}>위에서 평가 리스트 종류를 고르면 항목이 나옵니다.</div>
                ) : (
                    <>
                        {readOnly ? <div className={styles.readOnlyNote}>{viewName}님의 평가입니다. 보기만 가능하고, 내 평가는 "내 평가" 탭에서 입력합니다.</div> : null}
                        <div className={styles.evalTable}>
                            <div className={styles.evalTableHead}>
                                <div className={styles.evalTableTitle}>{category} 평가 리스트</div>
                                <div className={styles.evalTableSub}>평가 (ABCDE)</div>
                            </div>
                            <div className={styles.evalRows}>
                                {evalItems.map((text, i) => {
                                    const opts = text.indexOf("(P/F)") >= 0 ? ["P", "F"] : ["A", "B", "C", "D", "E"];
                                    return (
                                        <div key={i} className={styles.evalRow}>
                                            <div className={styles.evalText}><span className={styles.evalNo}>{i + 1}</span><span>{text}</span></div>
                                            <div className={styles.optRow}>
                                                {opts.map((o) => (
                                                    <button key={o} className={`${styles.optBtn} ${viewGrades[i] === o ? styles.optBtnActive : ""}`} disabled={readOnly} onClick={() => pickGrade(i, o)}>{o}</button>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </>
                )}

                <label className={styles.memoLabel}>메모 ({viewName})
                    <textarea className={styles.memo} rows={3} placeholder="면접 중 느낀 점, 특이사항" value={viewEval.memo || ""} readOnly={readOnly}
                        onChange={(e) => { if (!readOnly) changeMine({ memo: e.target.value }); }} />
                </label>

                <div className={styles.evalFoot}>
                    <span>내 평가는 입력하면 바로 자동 저장됩니다 (평가자: {myName}).</span>
                    <button className={styles.deleteBtn} onClick={remove}>삭제</button>
                </div>

                {capMsg ? <div className={styles.capMsg}>{capMsg}</div> : null}
                {capImg ? <img className={styles.capImg} src={capImg} alt="평가지 캡처 이미지" /> : null}
            </Box>
        </Modal>
    );
};

export default InterviewEvalModal;
